/* eslint eqeqeq: 1 */
/* eslint-disable max-lines */
import { action, observable, computed } from 'mobx';

import CookieStorageService from 'services/CookieStorageService';
import GamesService from 'services/GamesService/GamesService';
import messagingService from 'services/MessagingService/MessagingService';
import {
  CLEAR_GAME_RESULT,
  DISABLE_GAME,
  GAME_ACTION_DONE,
  GAME_INFO_RESPONSE,
  GAME_RESUMED,
  GAME_RESUME_ERROR,
  GAME_SAVED,
  GET_ACTIVE_GAME_INFO_ERROR,
  GET_ACTIVE_GAME_INFO_RESPONSE,
  GET_ACTIVE_GAME_INFO_TRIGGER,
  PLAY_RESPONSE,
  TIP_MENU_START,
  UPDATE_CURRENT_GAME,
} from 'services/MessagingService/EventNames';
import ChatStore from 'stores/ChatStore';
import FirebaseStore from 'stores/FirebaseStore';
import UserStore from 'stores/UserStore';
import APIStates from 'enum/APIStates';
import { NOTIFICATION_TYPE } from 'enum/messages';
import routeDefinitions from 'enum/routeDefinitions';
import allUserGroups from 'enum/userGroups';
import { YEAR_AS_SECONDS } from 'enum/timeFormat';
import {
  GAMES,
  GAMES_STATUS,
  GAME_OPERATIONS,
} from 'utils/consts';
import { chunkArray, isStringHTMLTag } from 'utils/helpers';


const AUTO_NOTIFICATION_DELAY_MILLIS = 300000;

class GamesStore {
  @observable activeGamesInfo = [];
  @observable rollTheDiceInfo = {};
  @observable prizes = null;
  @observable prizeList = [];
  @observable result = null;
  @observable pauseTime = null;
  @observable status = null;
  @observable prizeWinner = null;
  @observable gameName = '';
  @observable price = 0;
  @observable playStatus = APIStates.INITIAL;
  @observable tipMenuData = {};
  @observable isGameCenterOpen = false;
  @observable broadcasterRollTheDice = {};
  @observable broadcasterSpinTheWheel = {};
  @observable broadcasterTokenKeno = {};
  @observable broadcasterActiveGamesView = '';
  @observable isBroadcasterTipMenuOpen = false;
  @observable gameStatus = APIStates.INITIAL;
  @observable saveGameStatus = APIStates.INITIAL;
  @observable tipMenuStatus = APIStates.INITIAL;
  @observable gameResumeStatus = APIStates.INITIAL;
  @observable activeGamesInfoStatus = APIStates.INITIAL;

  gameNotificationCheckers = {};

  constructor() {
    this.messagingService = messagingService();
    this.gameService = new GamesService();
    this.roomName = '';
    this.token = '';
    this.gameNotificationCheckers = {};
    this.retryInterval = null;
    this.messagingService.on(GET_ACTIVE_GAME_INFO_ERROR, this.handleActiveGameInfoError);
  }

  init() {
    this.messagingService.on(GET_ACTIVE_GAME_INFO_RESPONSE, this._handleActiveGamesInfo);
    this.messagingService.on(GAME_INFO_RESPONSE, this.handleGameInfo);
    this.messagingService.on(UPDATE_CURRENT_GAME, this._updateCurrentGame);
    this.messagingService.on(CLEAR_GAME_RESULT, this.clearResult);
    this.messagingService.on(PLAY_RESPONSE, this._handlePlayResponse);
    this.messagingService.on(GET_ACTIVE_GAME_INFO_TRIGGER, this.getActiveGamesInfo);
    this.messagingService.on(TIP_MENU_START, this._handleTipMenuStart);
    this.messagingService.on(GAME_ACTION_DONE, this.handleGameActionDone);
    this.messagingService.on(GAME_SAVED, this.handleGameSaved);
    this.messagingService.on(GAME_RESUMED, this.handleGameResumed);
    this.messagingService.on(GAME_RESUME_ERROR, this.handleGameResumeError);
    this.messagingService.on(DISABLE_GAME, this.handleDisableGame);
  }

  destructor() {
    this.messagingService.off(GET_ACTIVE_GAME_INFO_RESPONSE, this._handleActiveGamesInfo);
    this.messagingService.off(UPDATE_CURRENT_GAME, this._updateCurrentGame);
    this.messagingService.off(CLEAR_GAME_RESULT, this.clearResult);
    this.messagingService.off(GAME_INFO_RESPONSE, this.handleGameInfo);
    this.messagingService.off(PLAY_RESPONSE, this._handlePlayResponse);
    this.messagingService.off(GET_ACTIVE_GAME_INFO_TRIGGER, this.getActiveGamesInfo);
    this.messagingService.off(TIP_MENU_START, this._handleTipMenuStart);
    this.messagingService.off(GAME_ACTION_DONE, this.handleGameActionDone);
    this.messagingService.off(GAME_SAVED, this.handleGameSaved);
    this.messagingService.off(GAME_RESUMED, this.handleGameResumed);
    this.messagingService.off(GAME_RESUME_ERROR, this.handleGameResumeError);
    this.messagingService.off(GET_ACTIVE_GAME_INFO_ERROR, this.handleActiveGameInfoError);
    this.messagingService.off(DISABLE_GAME, this.handleDisableGame);
  }

  @action handleGameCenterDrawer = (flag) => {
    this.isGameCenterOpen = flag;
  };

  @action getActiveGamesInfo = ({ roomName, token }) => {
    this.roomName = roomName;
    this.token = token;
    this.gameService.getActiveGamesInfo(roomName, token);
  };

  @action setRoleTheDicePrizeNames = (name, value) => {
    if (!isStringHTMLTag(value)) {
      this.broadcasterRollTheDice = { ...this.broadcasterRollTheDice, [name]: value };
    }
  };

  @action setTokenKenoPrizeNames = (name, value, isPrize) => {
    if (!isStringHTMLTag(value)) {
      if (value === '') {
        const deepCopy = Object.assign({}, this.broadcasterTokenKeno);
        if (isPrize) {
          if (deepCopy[name].p === undefined || deepCopy[name].p === 0) {
            delete deepCopy[name];
          } else {
            delete deepCopy[name].pr;
          }
        } else if (deepCopy[name].pr === undefined) {
          delete deepCopy[name];
        } else {
          delete deepCopy[name].p;
        }
        this.broadcasterTokenKeno = deepCopy;
      } else if (isPrize) {
        this.broadcasterTokenKeno = {
          ...this.broadcasterTokenKeno,
          [name]: {
            p: this.broadcasterTokenKeno[name]?.p,
            pr: value,
          },
        };
      } else {
        this.broadcasterTokenKeno = {
          ...this.broadcasterTokenKeno,
          [name]: {
            p: parseInt(value, 10) === 0 ? undefined : parseInt(value, 10),
            pr: this.broadcasterTokenKeno[name]?.pr,
          },
        };
      }
    }
  };

  @action setSpinTheWheelPrizeNames = (name, value) => {
    if (!isStringHTMLTag(value)) {
      if (value === '') {
        const deepCopy = Object.assign({}, this.broadcasterSpinTheWheel);
        delete deepCopy[name];
        this.broadcasterSpinTheWheel = deepCopy;
      } else {
        this.broadcasterSpinTheWheel = { ...this.broadcasterSpinTheWheel, [name]: value };
      }
    }
  };

  @action spinTheWheelRoll = (roomName, token) => {
    this.playStatus = APIStates.PENDING;
    const data = {
      roomName,
      token,
    };
    this.gameService.spinTheWheel.action(data);
  };

  @action setBroadcasterActiveView = (value) => {
    this.broadcasterActiveGamesView = value;
  };

  @action setBroadcasterTipMenu = (value) => {
    this.isBroadcasterTipMenuOpen = value;
  };

  @action _handleTipMenuStart = () => {
    this.tipMenuStatus = APIStates.LOADED;
    this.tipMenuEnabled = true;
  };

  @action _updateCurrentGame = (info) => {
    if (Object.keys(info).length === 0) {
      this.resetGameInfo();
    } else {
      if (info.s) {
        this.status = info.s;
      }
      if (info.ep) {
        this.pauseTime = info.ep;
      }
      if (info.o) {
        this.prizes = info.o;
      }
      if (info.pl) {
        this.prizeList = info.pl;
      }
      if (info.r) {
        this.result = info.r;
      }
      if (info.prizeWinner) {
        this.prizeWinner = info.prizeWinner;
      }
      if (info.gameName) {
        this.gameName = info.gameName;
        const latestUsedInteraction = CookieStorageService.get('actionButtonId');
        const interactionItems = ['crown', 'boost', 'sendGift'];
        if (latestUsedInteraction && !interactionItems.includes(latestUsedInteraction)) {
          CookieStorageService.set('actionButtonId', { buttonId: info.gameName, broadcaster: this.roomName },
            { maxAge: YEAR_AS_SECONDS });
        }
      }
      if (info.p) {
        this.price = info.p;
      }
    }
  };

  @action getGameInfo = (data) => {
    this.gameService.getGameInfo(data);
  };

  @action tokenKenoClick = (roomName, token, index) => {
    this.playStatus = APIStates.PENDING;
    const data = {
      roomName,
      token,
      index,
    };
    this.gameService.tokenKeno.action(data);
  };

  @action rollTheDiceClick = (roomName, token) => {
    this.playStatus = APIStates.PENDING;
    const data = {
      roomName,
      token,
    };
    this.gameService.rollTheDice.action(data);
  };

  @action handleGameActionDone = () => {
    this.gameStatus = APIStates.LOADED;
  };

  @action rollTheDiceStart = (data) => {
    this.gameStatus = APIStates.PENDING;
    this.gameService.rollTheDice.start(data);
  };

  @action rollTheDiceStop = (data) => {
    this.gameStatus = APIStates.PENDING;
    this.gameService.rollTheDice.stop(data);
  };

  @action spinTheWheelStart = (data) => {
    this.gameStatus = APIStates.PENDING;
    this.gameService.spinTheWheel.start(data);
  };

  @action spinTheWheelStop = (data) => {
    this.gameStatus = APIStates.PENDING;
    this.gameService.spinTheWheel.stop(data);
  };

  @action tokenKenoStart = (data) => {
    this.gameStatus = APIStates.PENDING;
    this.gameService.tokenKeno.start(data);
  };

  @action tokenKenoStop = (data) => {
    this.gameStatus = APIStates.PENDING;
    this.gameService.tokenKeno.stop(data);
  };

  handleGameStopStartAnotherGame = (data, currentGame, newGame) => {
    this.gameService.handleGameStopStartAnotherGame(data, currentGame, newGame);
  };

  @action clearResult = () => {
    this.result = null;
    this.prizeWinner = null;
  };

  @computed get activeGame() {
    return this.activeGamesInfo.filter((game) => game.name !== 'TippingBar')[0];
  }

  @computed get activeGameName() {
    return this.activeGamesInfo
      .filter((game) => game.name !== 'TippingBar')
      .map((filteredGame) => filteredGame && filteredGame.name)[0];
  }

  @computed get spinTheWheelActive() {
    return this.gameName === GAMES.SPIN_THE_WHEEL;
  }

  @computed get tokenKenoActive() {
    return this.gameName === GAMES.TOKEN_SQUARES;
  }

  @computed get rollTheDiceActive() {
    return this.gameName === GAMES.ROLL_THE_DICE;
  }

  @computed get anyGameActive() {
    return !!this.gameName;
  }

  @computed get isPlayActionLoading() {
    return this.playStatus === APIStates.PENDING;
  }

  @computed get gamePaused() {
    return this.pauseTime && this.status === GAMES_STATUS.PAUSE;
  }

  @computed get lowestPrice() {
    const availablePrizes = this.prizes && this.prizes.filter((el) => !el.dis).map((el) => el.p);
    const lowestPrice = availablePrizes && Math.min(...availablePrizes);

    return lowestPrice;
  }

  @computed get tipMenuActive() {
    return this.activeGamesInfo.find((el) => el.name === GAMES.TIP_MENU && this.isGameRunning(el));
  }

  @computed get tipMenuInitialData() {
    return this.activeGamesInfo.find((el) => el.name === GAMES.TIP_MENU);
  }

  @action _handleActiveGamesInfo = (activeGamesInfo) => {
    this.activeGamesInfoStatus = APIStates.LOADED;
    clearInterval(this.retryInterval);
    if (!activeGamesInfo) {
      return;
    }
    this.gamesInfoTime = new Date().getTime();
    this.activeGamesInfo = activeGamesInfo;
    this.gameName =
      activeGamesInfo
        .filter((game) => game.name !== 'TippingBar' && game.name !== GAMES.TIP_MENU)
        .map((filteredGame) => filteredGame && filteredGame.name)[0] || '';
    if (UserStore.hasCF(allUserGroups.BROADCAST_WINDOW_V2) && location.pathname === '/broadcast') {
      const spinTheWheelInfo = activeGamesInfo.find((x) => x.name === GAMES.SPIN_THE_WHEEL);
      const rollTheDiceInfo = activeGamesInfo.find((x) => x.name === GAMES.ROLL_THE_DICE);
      const tokenKenoInfo = activeGamesInfo.find((x) => x.name === GAMES.TOKEN_SQUARES);
      if (rollTheDiceInfo && rollTheDiceInfo.details.o) {
        this.broadcasterRollTheDice = {
          price: rollTheDiceInfo.details.p,
          1: rollTheDiceInfo.details.o[1],
          2: rollTheDiceInfo.details.o[2],
          3: rollTheDiceInfo.details.o[3],
          4: rollTheDiceInfo.details.o[4],
          5: rollTheDiceInfo.details.o[5],
          6: rollTheDiceInfo.details.o[6],
        };
        if (spinTheWheelInfo && spinTheWheelInfo.details.o) {
          const infoArray = Object.values(spinTheWheelInfo.details.o).map((item, key) => {
            return { [key]: item.o };
          });
          const prizeInfo = Object.assign({}, ...infoArray);
          this.broadcasterSpinTheWheel = {
            price: spinTheWheelInfo.details.p,
            ...prizeInfo,
          };
        }
      }
      if (tokenKenoInfo && tokenKenoInfo.details.o) {
        this.broadcasterTokenKeno = { ...tokenKenoInfo.details.o };
      }
    }
  };

  @action setRollTheDiceInfo = (rollTheDiceInfo) => {
    this.rollTheDiceInfo = rollTheDiceInfo;
  };

  @action handleGameInfo = (info) => {
    this.prizes = info.o;
    this.prizeList = info.pl;
    this.pauseTime = info.ep;
    this.status = info.s;
    this.result = info.r;
    this.price = info.p;
  };

  @action resetGameInfo = () => {
    this.prizes = null;
    this.prizeList = [];
    this.result = null;
    this.pauseTime = null;
    this.status = null;
    this.prizeWinner = null;
    this.gameName = '';
    this.price = 0;
    CookieStorageService.set('actionButtonId', { buttonId: 'sendGift' }, { maxAge: YEAR_AS_SECONDS });
  };

  @action _handlePlayResponse = () => {
    this.playStatus = APIStates.LOADED;
  };

  tipMenuAction = (data) => {
    this.gameService.tipMenu.action(data);
  };

  @action saveGameSettings = (data) => {
    this.saveGameStatus = APIStates.PENDING;
    this.gameService.saveGameSettings(data);
  };

  @action saveTipMenuSettings = async(data) => {
    this.saveGameStatus = APIStates.PENDING;
    await this.gameService.tipMenu.saveSettings(data);

    if (!this.tipMenuActive) {
      this.tipMenuStatus = APIStates.PENDING;
      const { token, username, ...payload } = data;
      await this.gameService.tipMenu.start(username, token, Object.values(payload));
    }
  };

  @action handleGameSaved = () => {
    this.saveGameStatus = APIStates.LOADED;
  };

  @action resumeGame = (roomName, gameName, token) => {
    this.gameResumeStatus = APIStates.PENDING;
    this.gameService.resumeGame(roomName, gameName, token);
  };

  @action handleGameResumed = () => {
    this.gameResumeStatus = APIStates.LOADED;
  };

  @action handleGameResumeError = () => {
    this.gameResumeStatus = APIStates.ERROR;
  };

  @action handleActiveGameInfoError = ({ roomName, token }) => {
    const DELAY = 60000;
    this.activeGamesInfoStatus = APIStates.ERROR;
    const isBwr = location.pathname === routeDefinitions.broadcaster;
    if (isBwr) {
      this.retryInterval = setInterval(() => {
        this.gameService.getActiveGamesInfo(roomName, token, true);
      }, DELAY);
    }
  };

  @computed get gamesInfoUnavailable() {
    return this.activeGamesInfoStatus === APIStates.ERROR;
  }

  @computed get rollTheDiceInfoEmpty() {
    return Object.keys(this.rollTheDiceInfo).length === 0;
  }

  @computed get tipMenuActions() {
    const tipMenu = this.activeGamesInfo.find((game) => game.name === GAMES.TIP_MENU);
    const actions = tipMenu ? tipMenu.details.o : [];

    return actions;
  }

  @computed get tipMenuActionsMobile() {
    let actions = [];
    const tipMenu = this.activeGamesInfo.find((game) => game.name === GAMES.TIP_MENU);
    if (tipMenu) {
      actions = tipMenu.details.o.map((el, index) => Object.assign(el, { index }));
    }
    const chunks = chunkArray(actions, 4);

    return chunks;
  }

  @computed get broadcasterActiveGame() {
    const games = this.activeGamesInfo.filter((el) =>
      [GAMES.ROLL_THE_DICE, GAMES.SPIN_THE_WHEEL, GAMES.TOKEN_SQUARES].includes(el.name)
    );

    return games.find((game) => game.details.s);
  }

  @computed get tipMenuGameActive() {
    return !!this.activeGamesInfo.find((el) => el.name === GAMES.TIP_MENU && this.isGameRunning(el));
  }

  checkActiveGamesAndStartAutoNotifications = () => {
    const { hasCF } = UserStore;
    if (!hasCF(allUserGroups.BROADCAST_WINDOW_V2)) {
      return;
    }
    this.notificationsToBeStarted.forEach(this.findStartTimestampAndStartAutoNotifications);
  };

  @computed get notificationsToBeStarted() {
    return this.activeGamesInfo.filter(this.shouldStartAutoNotification).map(this.makeNotificationDataFromGame);
  }

  makeNotificationDataFromGame = (game) => {
    return {
      type: NOTIFICATION_TYPE.GAME,
      gameName: game.name,
      notificationExtra: JSON.stringify({ ...game.details }),
    };
  };

  shouldStartAutoNotification = (game) =>
    this.isGameRunning(game) &&
    this.gameService.isGameWithNotifications(game) &&
    !this.gameNotificationCheckers[game.name];

  canAddNotification = () => {
    const { messages } = ChatStore;
    const lastThreeMesages = messages.slice(messages.length - 3, messages.length);

    return !lastThreeMesages.some(
      (message) => message.type === NOTIFICATION_TYPE.GAME || message.type === NOTIFICATION_TYPE.LIVE_TOUCH
    );
  };

  isGameRunning = ({ details: { s: gameStatus, st: serverTimestamp } }) =>
    gameStatus === GAMES_STATUS.IDLE && !!serverTimestamp;

  findStartTimestampAndStartAutoNotifications = (notification) => {
    const timestampFromEvents = this.findGameStartTimestamp(notification.gameName);
    const millisElapsed = Date.now() - timestampFromEvents;
    const delayPart = millisElapsed % AUTO_NOTIFICATION_DELAY_MILLIS;
    const initialDellayMillis = AUTO_NOTIFICATION_DELAY_MILLIS - delayPart;
    setTimeout(() => {
      this.addNotification(notification);
      this.startAutoNotifications(notification);
    }, initialDellayMillis);
  };

  startAutoNotifications = (notification) => {
    if (!this.gameNotificationCheckers[notification.gameName]) {
      this.gameNotificationCheckers[notification.gameName] = setInterval(() => {
        this.addNotification(notification);
      }, AUTO_NOTIFICATION_DELAY_MILLIS);
    }
  };

  findGameStartTimestamp = (gameName) => {
    const { messages } = ChatStore;
    const { broadcastStartTime } = FirebaseStore;
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const message = messages[i];
      if (message.gameName !== gameName) {
        continue;
      }
      if (message.gameOperation === GAME_OPERATIONS.START) {
        return message.timestamp;
      }
    }

    return broadcastStartTime;
  };

  addNotification = (notification) => {
    if (!this.canAddNotification()) {
      return;
    }
    ChatStore.addMessage({
      ...notification,
      timestamp: Date.now(),
    });
  };

  stopAutoNotifications = (notification) => {
    const interval = this.gameNotificationCheckers[notification.gameName];
    if (interval) {
      clearInterval(interval);
    }
    delete this.gameNotificationCheckers[notification.gameName];
  };

  isGameActive = (gameName) => computed(() => this.broadcasterActiveGame?.name === gameName).get();

  @computed get gameActionPending() {
    return this.gameStatus === APIStates.PENDING;
  }

  @computed get gameSavePending() {
    return this.saveGameStatus === APIStates.PENDING;
  }

  @computed get gameResumePending() {
    return this.gameResumeStatus === APIStates.PENDING;
  }

  handleDisableGame = () => {
    if (this.broadcasterActiveGame) {
      const data = {
        roomName: this.roomName?.toLowerCase(),
        token: this.token,
      };

      switch (this.broadcasterActiveGame.name) {
        case GAMES.ROLL_THE_DICE:
          this.rollTheDiceStop(data);
          break;
        case GAMES.SPIN_THE_WHEEL:
          this.spinTheWheelStop(data);
          break;
        case GAMES.TOKEN_SQUARES:
          this.tokenKenoStop(data);
          break;
        default:
          break;
      }
    }
  };
}

export default new GamesStore();
